import { useQueryClient } from '@tanstack/react-query';
import { useState, useCallback } from 'react';

import queryKeys from '@pray/shared/queryKeys';
import {
  startYouTubeOAuth,
  handleYouTubeOAuthCallback,
  disconnectYouTubeAccount,
} from '@pray/shared/services/studioService/socials';
import logger from '@pray/shared/utils/logger';

type UseYouTubeOAuthProps = {
  artistId: string;
};

type UseYouTubeOAuthReturn = {
  isConnecting: boolean;
  isDisconnecting: boolean;
  error: string | null;
  connectYouTube: () => Promise<void>;
  disconnectYouTube: () => Promise<void>;
  handleOAuthCallback: (code: string, state: string) => Promise<boolean>;
  clearError: () => void;
};

export default function useYouTubeOAuth({ artistId }: UseYouTubeOAuthProps): UseYouTubeOAuthReturn {
  const queryClient = useQueryClient();
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.artistYouTubeConnectionStatus(artistId),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.artistConnectedSocialPlatforms(artistId),
    });
  }, [artistId, queryClient]);

  const connectYouTube = useCallback(async () => {
    if (!artistId) {
      setError('Artist ID is required');
      return;
    }

    try {
      setIsConnecting(true);
      setError(null);

      const response = await startYouTubeOAuth({ artistId });

      const { oauth_url: oauthUrl } = response.data;

      const popup = window.open(oauthUrl, 'youtube-oauth', 'width=600,height=600,scrollbars=yes,resizable=yes');

      // Monitor the popup window and refresh when it closes
      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed);
          setIsConnecting(false);
          invalidateQueries();
        }
      }, 1000);
    } catch (err) {
      logger.error('Failed to initiate YouTube OAuth', err);
      setError('Failed to connect to YouTube. Please try again.');
      setIsConnecting(false);
    }
  }, [artistId, invalidateQueries]);

  const disconnectYouTube = useCallback(async () => {
    if (!artistId) {
      setError('Artist ID is required');
      return;
    }

    try {
      setIsDisconnecting(true);
      setError(null);

      const response = await disconnectYouTubeAccount({ artistId });

      if (response.is_connected === false) {
        invalidateQueries();
      }
    } catch (err) {
      logger.error('Failed to disconnect YouTube account', err);
      setError('Failed to disconnect YouTube account. Please try again.');
    } finally {
      setIsDisconnecting(false);
    }
  }, [artistId, invalidateQueries]);

  const handleOAuthCallback = async (code: string, state: string): Promise<boolean> => {
    if (!artistId) {
      setError('Artist ID is required');
      return false;
    }

    try {
      setError(null);

      const response = await handleYouTubeOAuthCallback({ artistId, code, state });
      const { is_connected: isConnected, error: oauthError } = response.data;

      if (oauthError) {
        throw new Error(oauthError);
      }

      if (isConnected) {
        return true;
      }
      throw new Error('Failed to connect YouTube account');
    } catch (err) {
      logger.error('Failed to complete YouTube OAuth callback', err);
      setError('Failed to complete YouTube connection. Please try again.');
      return false;
    }
  };

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    isConnecting,
    isDisconnecting,
    error,
    connectYouTube,
    disconnectYouTube,
    handleOAuthCallback,
    clearError,
  };
}
